import React, { useEffect, useState } from "react";
import {
  FormBuilderProps,
  FormSchema as FormBuilderSchema,
  ExtraField,
  FormContentProps,
} from "../../interfaces/components/form";
import { FormItem } from "./fields/FormItem";
import { FormProvider, useForm } from "./FormContext";
import { store } from "../../redux/store";
import { hideModal } from "../../connected-components/modal/reducer";
import Button from "../button";
import { ButtonVariant } from "../../shared/utils/enums";
import { formComponents } from "./FormComponents";
import { TextareaFieldComponent } from "./fields/TextareaField";
import { InputFieldComponent } from "./fields/InputField";


const FormContent: React.FC<FormContentProps> = ({
  schema,
  onSubmit,
  className,
  gridConfig,
  submitButtonClassName,
  initialValues,
  buttonType = "cancel-save",
  acceptExtraFields = false,
  initExtraFields = [],
  resetOnSubmit = true,
}) => {
  const { fields, initializeFields, resetForm, validateForm, setFieldValue } = useForm();

  const [extraFields, setExtraFields] = useState<ExtraField[]>(initExtraFields);
  const [showNewFieldForm, setShowNewFieldForm] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");

  const onCancel = () => {
    store.dispatch(hideModal());
  };

  // Group fields by section
  const groupedFields = Object.entries(schema.properties).reduce(
    (acc, [key, field]) => {
      const section = field.section || "General";
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push([key, field]);
      return acc;
    },
    {} as Record<string, [string, FormBuilderSchema["properties"][string]][]>,
  );

  // Initialize form fields
  useEffect(() => {
    const initialFields = Object.entries(schema.properties).reduce(
      (acc, [key, field]) => {
        const typedField = field as FormBuilderSchema["properties"][string];
        return {
          ...acc,
          [key]: {
            value: initialValues?.[key] || "",
            touched: false,
            errors: [],
            required: typedField.required || false,
            label: typedField.label,
            type: typedField.componentProps?.type,
            format: typedField.componentProps?.format,
            validator: typedField.validator,
            min_value: typedField.componentProps?.min,
            max_value: typedField.componentProps?.max,
            step_value: typedField.componentProps?.step,
            disabled: typedField.componentProps?.disabled,
          },
        };
      },
      {},
    );

    initializeFields(initialFields);
  }, [schema, initializeFields, initialValues]);

  // Generate grid classes based on configuration
  const getGridClasses = () => {
    const { desktop = 2 } = gridConfig || { desktop: 2 };
    const desktopClasses =
      {
        1: "md:grid-cols-1",
        2: "md:grid-cols-2",
        3: "md:grid-cols-3",
        4: "md:grid-cols-4",
      }[desktop] || "md:grid-cols-2";

    return `grid grid-cols-1 ${desktopClasses} gap-4`;
  };

  const handleSubmit = async () => {
    try {
      const isValid = validateForm();

      if (!isValid) {
        return;
      }

      if (onSubmit) {
        const values: Record<string, any> = Object.entries(fields).reduce(
          (acc, [key, field]) => ({
            ...acc,
            [key]: field.value,
          }),
          {} as Record<string, any>,
        );

        // Add extra fields to the submitted values if enabled
        if (acceptExtraFields && extraFields.length > 0) {
          values.extra_fields = extraFields;
        }

        await onSubmit(values);

        // Only reset if resetOnSubmit is true
        if (resetOnSubmit) {
          resetForm();
          if (acceptExtraFields) {
            setExtraFields([]);
          }
        }
      }
    } catch (err) {
      console.error("Form submission error:", err);
    }
  };

  const handleAddField = () => {
    if (newFieldLabel.trim() === "") return;

    // Create a key from the label (lowercase, replace spaces with underscores)
    const key = newFieldLabel.toLowerCase().replace(/\s+/g, "_");

    setExtraFields([
      ...extraFields,
      {
        label: newFieldLabel,
        key,
        value: newFieldValue,
        type: "textarea",
      },
    ]);

    // Reset form
    setNewFieldLabel("");
    setNewFieldValue("");
    setShowNewFieldForm(false);
  };

  const shouldShowField = (field: FormBuilderSchema["properties"][string]) => {
    if (!field.conditions || field.conditions.length === 0) {
      return true;
    }
    return field.conditions.every((condition) => {
      const targetField = fields[condition.field];
      if (!targetField) return false;
      if (Array.isArray(condition.value)) {
        return condition.value.includes(targetField.value);
      }
      return targetField.value === condition.value;
    });
  };

  const showSectionTitles = Object.keys(groupedFields).length > 1;

  return (
    <div className={`w-full h-full ${className}`}>
      {Object.entries(groupedFields).map(([section, sectionFields]) => (
        <div key={section} className="mb-8">
          {showSectionTitles && (
            <h2 className="text-xl font-semibold mb-4 pb-2 border-b">
              {section}
            </h2>
          )}
          <div className={getGridClasses()}>
            {sectionFields.map(([key, field]) => {
              const typedField =
                field as FormBuilderSchema["properties"][string];

              if (!shouldShowField(typedField)) {
                return null;
              }

              const Component = formComponents[typedField.component];
              if (!Component) {
                console.warn(
                  `Component ${typedField.component} not found for field ${key}`,
                );
                return null;
              }

              return (
                <div key={key} className="col-span-1">
                  <FormItem
                    label={typedField.label}
                    required={typedField.required}
                    helperText={typedField.helperText}
                  >
                    <Component
                      {...typedField.componentProps}
                      name={key}
                      label={typedField.label}
                      required={typedField.required}
                      format={typedField.componentProps?.format}
                      options={
                        typedField.options ||
                        typedField.componentProps?.options ||
                        []
                      }
                      onChange={(value: any) => setFieldValue(key, value)}
                      mode={typedField.componentProps?.mode}
                      showTime={typedField.componentProps?.showTime}
                      disabled={typedField.componentProps?.disabled}
                      value={fields[key]?.value}
                      min={typedField.componentProps?.min}
                      max={typedField.componentProps?.max}
                      step={typedField.componentProps?.step}
                    />
                  </FormItem>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {acceptExtraFields && (
        <div className="border-t border-gray-200 pt-4 mt-6">
          <h3 className="text-lg font-medium mb-4">Custom Fields</h3>

          {extraFields.map((field, index) => (
            <div
              key={index}
              className="mb-5 border border-gray-200 dark:border-gray-700 p-5 rounded-md bg-white dark:bg-gray-800"
            >
              <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                <h4 className="font-medium text-gray-800 dark:text-gray-200">
                  {field.label}
                </h4>
                <Button
                  variant={ButtonVariant.CLEAR}
                  onClick={() => {
                    const updatedFields = [...extraFields];
                    updatedFields.splice(index, 1);
                    setExtraFields(updatedFields);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 border border-transparent hover:border-red-100 dark:hover:border-red-800"
                  aria-label="Remove field"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  <span>Remove</span>
                </Button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/40 rounded-md border border-gray-100 dark:border-gray-700">
                <TextareaFieldComponent
                  name={`extra_field_${index}`}
                  value={field.value}
                  onChange={(value) => {
                    const updatedFields = [...extraFields];
                    updatedFields[index].value = value;
                    setExtraFields(updatedFields);
                  }}
                  rows={3}
                  className="bg-transparent focus:ring-primary"
                />
              </div>
            </div>
          ))}

          {showNewFieldForm ? (
            <div className="mb-5 border-2 border-primary/20 dark:border-primary/30 p-5 rounded-md bg-white dark:bg-gray-800">
              <div className="mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">
                <h4 className="font-medium text-gray-800 dark:text-gray-200">
                  New Custom Field
                </h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <FormItem label="Field Name" required>
                    <InputFieldComponent
                      name="new_field_label"
                      value={newFieldLabel}
                      onChange={setNewFieldLabel}
                      placeholder="Enter field name"
                      className="bg-gray-50 dark:bg-gray-900/40 focus:ring-primary"
                    />
                  </FormItem>
                </div>
                <div>
                  <FormItem label="Field Value" required>
                    <TextareaFieldComponent
                      name="new_field_value"
                      value={newFieldValue}
                      onChange={setNewFieldValue}
                      placeholder="Enter field value"
                      rows={3}
                      className="bg-gray-50 dark:bg-gray-900/40 focus:ring-primary"
                    />
                  </FormItem>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => setShowNewFieldForm(false)}
                  className="px-4 py-2 text-sm bg-transparent border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-md transition-colors duration-200 dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
                >
                  Cancel
                </Button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  onClick={handleAddField}
                  className="px-4 py-2 text-sm bg-primary hover:bg-blue-600 text-white font-medium rounded-md transition-colors duration-200 dark:bg-blue-700 dark:hover:bg-blue-800 flex items-center gap-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Field
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={() => setShowNewFieldForm(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-medium rounded-md transition-colors duration-200 dark:bg-transparent dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add New Field
            </Button>
          )}
        </div>
      )}

      {buttonType === "submit" ? (
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={handleSubmit}
          className={`submit-button my-6 w-full text-sm lg:w-[auto] bg-primary hover:bg-blue-600 text-white font-bold py-1.5 px-[5%] rounded-xl dark:bg-blue-700 dark:hover:bg-blue-800 ${submitButtonClassName}`}
        >
          Submit
        </Button>
      ) : (
        <div className="flex justify-end gap-4 my-6">
          <Button
            variant={ButtonVariant.SECONDARY}
            onClick={onCancel}
            className="min-w-[120px] text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1.5 px-4 rounded-xl dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={handleSubmit}
            className={`min-w-[120px] text-sm bg-primary hover:bg-blue-600 text-white font-bold py-1.5 px-4 rounded-xl dark:bg-blue-700 dark:hover:bg-blue-800 ${submitButtonClassName}`}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export const FormBuilder: React.FC<FormBuilderProps> = (props) => {
  return (
    <FormProvider>
      <FormContent {...props} />
    </FormProvider>
  );
};
